// frontend/src/components/routineWizard/RoutineWizard.js
import React, { useReducer, useState, useCallback, useEffect, useMemo } from 'react';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { ArrowLeft, X, Sparkles, Lock, Dumbbell, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api';
import { buildWizardPrompt, difficultyForExperience } from './wizardPrompt';
import { TA } from '../tour/tourAnchors';
import { useTourEvent } from '../tour/tourEventBus';

const STEPS = ['experience', 'equipment', 'objective', 'days', 'time', 'specifics'];

const initialState = {
  step: 'experience',
  answers: {
    experience: null,
    equipment: { primary: null, specific: [] },
    objective: null,
    days: null,
    time: null,
    specifics: '',
  },
  result: null,
  error: null,
  generateAttempt: 0,
};

function reducer(state, action) {
  switch (action.type) {
    case 'set_answer':
      return { ...state, answers: { ...state.answers, [action.key]: action.value } };
    case 'set_step':
      return { ...state, step: action.step };
    case 'set_result':
      return { ...state, result: action.result, error: null, step: 'preview' };
    case 'set_error':
      return { ...state, error: action.error };
    case 'retry_generation':
      return { ...state, error: null, result: null, generateAttempt: (state.generateAttempt || 0) + 1, step: 'generating' };
    case 'reset':
      return initialState;
    default:
      return state;
  }
}

function isStepValid(step, answers) {
  if (step === 'experience') return !!answers.experience;
  if (step === 'equipment') {
    return !!answers.equipment.primary || answers.equipment.specific.length > 0;
  }
  if (step === 'objective') return !!answers.objective;
  if (step === 'days') return !!answers.days;
  if (step === 'time') return !!answers.time;
  if (step === 'specifics') return true; // optional — always valid
  return false;
}

function nextStep(step) {
  const i = STEPS.indexOf(step);
  if (i < 0) return step;
  if (i === STEPS.length - 1) return 'generating';
  return STEPS[i + 1];
}

function prevStep(step) {
  const i = STEPS.indexOf(step);
  if (i <= 0) return step;
  return STEPS[i - 1];
}

function OptionCard({ active, onClick, disabled, icon, title, subtitle, badge, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || title}
      className={[
        'w-full text-left rounded-xl p-4 min-h-[64px] transition-all duration-200',
        'bg-card/40 backdrop-blur-sm',
        active
          ? 'border-2 border-primary bg-primary/10 shadow-[0_0_16px_-6px_rgba(255,165,0,0.5)] scale-[1.02]'
          : 'border border-border/50 hover:border-border hover:bg-card/60',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        {icon && (
          <div className={`text-xl flex-shrink-0 ${active ? 'scale-110 transition-transform' : ''}`}>{icon}</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {title?.toUpperCase()}
            </span>
            {badge}
          </div>
          {subtitle && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{subtitle}</p>}
        </div>
      </div>
    </button>
  );
}

const SPECIFIC_ITEMS = [
  'barbell', 'squatRack', 'bench', 'adjustableDumbbells', 'fixedDumbbells',
  'kettlebells', 'cable', 'smith', 'legPress', 'bands', 'pullupBar', 'dipStation', 'cardio',
];

function SpecificsStep({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {t('routines.wizard.step.specifics.title')}
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          {t('routines.wizard.step.specifics.subtitle')}
        </p>
      </div>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('routines.wizard.step.specifics.placeholder')}
        maxLength={500}
        rows={6}
        className="w-full rounded-xl border border-border/50 bg-card/40 backdrop-blur-sm p-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none"
        data-testid="wizard-specifics-textarea"
      />
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>{t('routines.wizard.step.specifics.optional')}</span>
        <span>{(value || '').length}/500</span>
      </div>
    </div>
  );
}

function EquipmentStep({ value, onChange }) {
  const { t } = useTranslation();
  const [specificOpen, setSpecificOpen] = useState(false);

  const primaryOptions = [
    { key: 'full_gym', icon: '🏋', labelKey: 'routines.wizard.step.equipment.fullGym' },
    { key: 'dumbbells', icon: '🔩', labelKey: 'routines.wizard.step.equipment.dumbbells' },
    { key: 'bands', icon: '🪢', labelKey: 'routines.wizard.step.equipment.bands' },
    { key: 'bodyweight', icon: '🤸', labelKey: 'routines.wizard.step.equipment.bodyweight' },
  ];

  const hasSpecific = value.specific && value.specific.length > 0;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {t('routines.wizard.step.equipment.title')}
      </h2>

      <div className="space-y-2">
        {hasSpecific ? (
          <OptionCard
            active
            icon="🛠"
            onClick={() => setSpecificOpen(true)}
            title={t('routines.wizard.step.equipment.customCount', { count: value.specific.length })}
            subtitle={value.specific.map(k => t(`routines.wizard.step.equipment.items.${k}`)).join(', ')}
          />
        ) : (
          primaryOptions.map((o) => (
            <OptionCard
              key={o.key}
              active={value.primary === o.key}
              onClick={() => onChange({ primary: o.key, specific: [] })}
              icon={o.icon}
              title={t(o.labelKey)}
            />
          ))
        )}
      </div>

      <button
        type="button"
        onClick={() => setSpecificOpen(true)}
        className="w-full text-center text-xs text-primary hover:underline py-2"
        data-testid="wizard-equipment-specific-open"
      >
        {hasSpecific
          ? `↻ ${t('routines.wizard.step.equipment.chooseSpecific')}`
          : `→ ${t('routines.wizard.step.equipment.chooseSpecific')}`}
      </button>

      <SpecificEquipmentSheet
        open={specificOpen}
        onOpenChange={setSpecificOpen}
        value={value.specific}
        onChange={(specific) => onChange({ ...value, specific })}
      />
    </div>
  );
}

function SpecificEquipmentSheet({ open, onOpenChange, value, onChange }) {
  const { t } = useTranslation();
  const toggle = (k) => {
    if (value.includes(k)) onChange(value.filter(x => x !== k));
    else onChange([...value, k]);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[80dvh] sm:max-w-md sm:mx-auto sm:rounded-t-2xl p-0 flex flex-col">
        <SheetTitle asChild>
          <div className="px-5 py-4 border-b border-border/30">
            <h3 className="font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('routines.wizard.step.equipment.specificTitle')}
            </h3>
          </div>
        </SheetTitle>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1">
          {SPECIFIC_ITEMS.map(k => (
            <label
              key={k}
              className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-muted/50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={value.includes(k)}
                onChange={() => toggle(k)}
                className="h-4 w-4 rounded border-border accent-primary"
                data-testid={`wizard-eq-specific-${k}`}
              />
              <span className="text-sm">{t(`routines.wizard.step.equipment.items.${k}`)}</span>
            </label>
          ))}
        </div>
        <div className="border-t border-border/30 p-4">
          <Button onClick={() => onOpenChange(false)} size="lg" className="w-full">
            {t('routines.wizard.step.equipment.specificDone')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ObjectiveStep({ value, onChange }) {
  const { t } = useTranslation();
  const options = [
    { key: 'muscle',   icon: '💪', tKey: 'muscle' },
    { key: 'fat_loss', icon: '🔥', tKey: 'fatLoss' },
    { key: 'strength', icon: '🏋', tKey: 'strength' },
    { key: 'general',  icon: '⚡', tKey: 'general' },
  ];
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {t('routines.wizard.step.objective.title')}
      </h2>
      <div className="space-y-2">
        {options.map((o) => (
          <OptionCard
            key={o.key}
            active={value === o.key}
            onClick={() => onChange(o.key)}
            icon={o.icon}
            title={t(`routines.wizard.step.objective.${o.tKey}.title`)}
            subtitle={t(`routines.wizard.step.objective.${o.tKey}.subtitle`)}
          />
        ))}
      </div>
    </div>
  );
}

function DaysStep({ value, onChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isFreeUser } = useSubscription() || {};
  const isFree = isFreeUser?.() ?? false;

  const handleClick = (n) => {
    if (n === 7 && isFree) {
      navigate('/pricing');
      return;
    }
    onChange(n);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {t('routines.wizard.step.days.title')}
      </h2>
      <div className="grid grid-cols-5 gap-2">
        {[3, 4, 5, 6, 7].map((n) => {
          const locked = n === 7 && isFree;
          const active = value === n && !locked;
          return (
            <button
              key={n}
              type="button"
              onClick={() => handleClick(n)}
              className={[
                'relative h-16 rounded-xl border transition-all duration-200',
                'bg-card/40 backdrop-blur-sm font-bold text-xl',
                active
                  ? 'border-2 border-primary bg-primary/10 shadow-[0_0_16px_-6px_rgba(255,165,0,0.5)] scale-[1.05]'
                  : 'border-border/50 hover:border-border hover:bg-card/60',
                locked ? 'opacity-60' : '',
              ].join(' ')}
              data-testid={`wizard-days-${n}${locked ? '-locked' : ''}`}
              title={locked ? t('routines.wizard.step.days.proLock') : undefined}
            >
              {n}
              {locked && (
                <Lock className="absolute top-1 right-1 h-3 w-3 text-amber-400" />
              )}
            </button>
          );
        })}
      </div>
      {isFree && (
        <p className="text-[10px] text-muted-foreground text-center">
          {t('routines.wizard.step.days.proLock')}
        </p>
      )}
    </div>
  );
}

function TimeStep({ value, onChange }) {
  const { t } = useTranslation();
  const options = [
    { val: 30, key: 'min30' },
    { val: 45, key: 'min45' },
    { val: 60, key: 'min60' },
    { val: 90, key: 'min90' },
  ];
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {t('routines.wizard.step.time.title')}
      </h2>
      <div className="space-y-2">
        {options.map((o) => (
          <OptionCard
            key={o.val}
            active={value === o.val}
            onClick={() => onChange(o.val)}
            title={t(`routines.wizard.step.time.${o.key}.label`)}
            subtitle={t(`routines.wizard.step.time.${o.key}.subtitle`)}
          />
        ))}
      </div>
    </div>
  );
}

function GeneratingStep({ answers, onResult, onError }) {
  const { t } = useTranslation();
  const [statusIdx, setStatusIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setStatusIdx((i) => (i + 1) % 4), 1800);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prompt = buildWizardPrompt(answers, t);
        const res = await apiClient.post('/routines/generate-ai', {
          prompt,
          days_count: answers.days,
          difficulty: difficultyForExperience(answers.experience),
          focus: null,
          specifics: (answers.specifics || '').trim() || null,
        });
        if (!cancelled) onResult(res.data);
      } catch (err) {
        if (!cancelled) onError(err.response?.data?.detail || err.message || 'unknown');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusKey = `routines.wizard.generating.status${statusIdx + 1}`;

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-5 py-8 text-center">
      <div className="relative mb-6">
        <div className="absolute inset-0 rounded-full bg-primary/20 blur-2xl animate-pulse" />
        <Dumbbell className="relative h-14 w-14 text-primary animate-bounce" style={{ animationDuration: '1.5s' }} />
      </div>
      <h2 className="text-lg font-bold mb-3 leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {t('routines.wizard.generating.headline')}
      </h2>
      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="wizard-generating-status">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span key={statusKey} className="animate-in fade-in">{t(statusKey)}</span>
      </div>
    </div>
  );
}

function PreviewStep({ result, onSave, onTryAgain, onEditFirst, isFreeUser, maxDays }) {
  const { t } = useTranslation();
  const [expandedDay, setExpandedDay] = useState(0);

  // Defensive truncation: AI prompt encodes the day count, but if it returned more days than allowed, clip.
  const days = result?.days || [];
  const trimmedDays = (isFreeUser && days.length > maxDays) ? days.slice(0, maxDays) : days;
  const truncated = trimmedDays.length < days.length;

  const totalExercises = trimmedDays.reduce((acc, d) => acc + (d.exercises?.length || 0), 0);
  const totalSets = trimmedDays.reduce((acc, d) =>
    acc + (d.exercises || []).reduce((a, ex) => a + (ex.sets?.length || ex.sets || 0), 0), 0);

  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wider text-primary mb-1">
          <Sparkles className="h-3.5 w-3.5" />
          {t('routines.wizard.preview.headline')}
        </div>
        <h2 className="text-xl font-bold leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {result?.name || 'AI Generated Routine'}
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          {t('routines.wizard.preview.summary', {
            days: trimmedDays.length,
            exercises: totalExercises,
            sets: totalSets,
          })}
        </p>
      </div>

      {truncated && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          {t('routines.wizard.chooser.limitReached.banner', { max: maxDays })}
        </div>
      )}

      <div className="space-y-2">
        {trimmedDays.map((day, idx) => {
          const isOpen = expandedDay === idx;
          return (
            <div key={day.id || idx} className="rounded-xl border border-border/50 overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedDay(isOpen ? -1 : idx)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/30"
              >
                <span className="inline-flex h-5 w-5 items-center justify-center rounded bg-primary/15 text-primary text-[11px] font-bold">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {day.day_name?.toUpperCase() || t('routines.wizard.preview.dayLabel', { n: idx + 1 })}
                  </div>
                </div>
                <span className="text-[11px] text-muted-foreground">{day.exercises?.length || 0}</span>
              </button>
              {isOpen && (
                <div className="border-t border-border/30 px-3 py-2 space-y-1.5 bg-muted/10">
                  {(day.exercises || []).map((ex, exIdx) => (
                    <div key={ex.id || exIdx} className="flex items-center gap-2 text-xs">
                      <span className="flex-1 truncate">{ex.name}</span>
                      <span className="text-muted-foreground">
                        {Array.isArray(ex.sets) ? ex.sets.length : (ex.sets || 0)}×{ex.reps || `${ex.target_reps_min || 8}-${ex.target_reps_max || 12}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="space-y-2 pt-2">
        <Button
          onClick={() => onSave(trimmedDays)}
          size="lg"
          className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-500/90 hover:to-orange-600/90 shadow-[0_0_20px_-5px_rgba(255,165,0,0.6)]"
          data-testid="wizard-save-btn"
          data-tour-anchor={TA.WIZARD_SAVE_BTN}
        >
          {t('routines.wizard.preview.save')}
        </Button>
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={onTryAgain} variant="outline" size="sm" data-testid="wizard-try-again-btn">
            {t('routines.wizard.preview.tryAgain')}
          </Button>
          <Button onClick={() => onEditFirst(trimmedDays)} variant="outline" size="sm" data-testid="wizard-edit-first-btn">
            {t('routines.wizard.preview.editFirst')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ExperienceStep({ value, onChange }) {
  const { t } = useTranslation();
  const options = [
    { key: 'beginner', icon: '🌱' },
    { key: 'intermediate', icon: '💪' },
    { key: 'advanced', icon: '🔥' },
  ];
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {t('routines.wizard.step.experience.title')}
      </h2>
      <div className="space-y-2">
        {options.map((o) => (
          <OptionCard
            key={o.key}
            active={value === o.key}
            onClick={() => onChange(o.key)}
            icon={o.icon}
            title={t(`routines.wizard.step.experience.${o.key}.title`)}
            subtitle={t(`routines.wizard.step.experience.${o.key}.subtitle`)}
          />
        ))}
      </div>
    </div>
  );
}

export default function RoutineWizard({ open, onOpenChange, mode = 'personal', onSave }) {
  const { t } = useTranslation();
  const { isFreeUser } = useSubscription() || {};
  const isFree = isFreeUser?.() ?? false;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const wizardStepEventPayload = useMemo(
    () => state.step ? { step: state.step } : undefined,
    [state.step]
  );
  useTourEvent('wizard.step', wizardStepEventPayload);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      const tid = setTimeout(() => dispatch({ type: 'reset' }), 300);
      return () => clearTimeout(tid);
    }
  }, [open]);

  const hasAnswers = !!(state.answers.experience || state.answers.objective || state.answers.days);
  const stepIndex = STEPS.indexOf(state.step);
  const isQuestionStep = stepIndex >= 0;
  const showHeaderFooter = isQuestionStep;

  const handleClose = useCallback(() => {
    if (hasAnswers && state.step !== 'preview') {
      setConfirmCloseOpen(true);
    } else {
      onOpenChange?.(false);
    }
  }, [hasAnswers, state.step, onOpenChange]);

  const handleContinue = () => {
    if (!isStepValid(state.step, state.answers)) return;
    dispatch({ type: 'set_step', step: nextStep(state.step) });
  };

  const handleBack = () => {
    if (stepIndex > 0) dispatch({ type: 'set_step', step: prevStep(state.step) });
  };

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) handleClose(); else onOpenChange?.(true); }}>
        <SheetContent
          side="bottom"
          className="h-[100dvh] sm:h-auto sm:max-h-[90dvh] sm:max-w-lg sm:mx-auto sm:rounded-2xl p-0 flex flex-col gap-0 border-border/50"
          data-tour-anchor={TA.WIZARD_SHEET}
        >
          <SheetTitle className="sr-only">trainedbyRS Trainer</SheetTitle>
          {showHeaderFooter && (
            <div className="flex items-center gap-2 px-4 py-3 border-b border-border/30">
              <Button
                size="icon"
                variant="ghost"
                onClick={handleBack}
                disabled={stepIndex === 0}
                className="h-8 w-8"
                data-testid="wizard-back-btn"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex-1 text-center text-xs text-muted-foreground">
                {t('routines.wizard.step.stepOf', { current: stepIndex + 1, total: STEPS.length })}
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={handleClose}
                className="h-8 w-8"
                data-testid="wizard-close-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {showHeaderFooter && (
            <div className="h-1 bg-muted">
              <div
                className="h-full bg-gradient-to-r from-primary to-orange-400 transition-all duration-300"
                style={{ width: `${((stepIndex + 1) / STEPS.length) * 100}%` }}
              />
            </div>
          )}

          {/* Step body */}
          <div className="flex-1 overflow-y-auto px-5 py-6 animate-in fade-in slide-in-from-right-2 duration-300" key={state.step}>
            {state.step === 'experience' && (
              <ExperienceStep value={state.answers.experience} onChange={(v) => dispatch({ type: 'set_answer', key: 'experience', value: v })} />
            )}
            {state.step === 'equipment' && (
              <EquipmentStep value={state.answers.equipment} onChange={(v) => dispatch({ type: 'set_answer', key: 'equipment', value: v })} />
            )}
            {state.step === 'objective' && (
              <ObjectiveStep value={state.answers.objective} onChange={(v) => dispatch({ type: 'set_answer', key: 'objective', value: v })} />
            )}
            {state.step === 'days' && (
              <DaysStep value={state.answers.days} onChange={(v) => dispatch({ type: 'set_answer', key: 'days', value: v })} />
            )}
            {state.step === 'time' && (
              <TimeStep value={state.answers.time} onChange={(v) => dispatch({ type: 'set_answer', key: 'time', value: v })} />
            )}
            {state.step === 'specifics' && (
              <SpecificsStep value={state.answers.specifics} onChange={(v) => dispatch({ type: 'set_answer', key: 'specifics', value: v })} />
            )}
            {state.step === 'generating' && !state.error && (
              <GeneratingStep
                key={state.generateAttempt}
                answers={state.answers}
                onResult={(r) => dispatch({ type: 'set_result', result: r })}
                onError={(e) => dispatch({ type: 'set_error', error: e })}
              />
            )}
            {state.step === 'generating' && state.error && (
              <div className="mt-6 text-center space-y-3">
                <p className="text-sm text-destructive">{t('routines.wizard.error.generationFailed')}</p>
                <div className="flex gap-2 justify-center">
                  <Button onClick={() => dispatch({ type: 'retry_generation' })} size="sm" data-testid="wizard-error-retry">
                    {t('routines.wizard.error.tryAgain')}
                  </Button>
                  <Button onClick={handleClose} size="sm" variant="outline">
                    {t('routines.wizard.error.cancel')}
                  </Button>
                </div>
              </div>
            )}
            {state.step === 'preview' && state.result && (
              <PreviewStep
                result={state.result}
                isFreeUser={isFree}
                maxDays={6}
                onSave={(trimmedDays) => onSave?.({ ...state.result, days: trimmedDays }, mode)}
                onTryAgain={() => dispatch({ type: 'retry_generation' })}
                onEditFirst={(trimmedDays) => onSave?.({ ...state.result, days: trimmedDays }, mode, { editFirst: true })}
              />
            )}
          </div>

          {showHeaderFooter && (
            <div className="border-t border-border/30 p-4">
              <Button
                onClick={handleContinue}
                disabled={!isStepValid(state.step, state.answers)}
                size="lg"
                className={`w-full ${state.step === 'specifics' ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-500/90 hover:to-orange-600/90 shadow-[0_0_20px_-5px_rgba(255,165,0,0.6)]' : 'bg-primary hover:bg-primary/90'}`}
                data-testid="wizard-continue-btn"
              >
                {state.step === 'specifics' ? (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    {t('routines.wizard.step.generate')}
                  </>
                ) : (
                  t('routines.wizard.step.continue')
                )}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('routines.wizard.closeConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('routines.wizard.closeConfirm.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('routines.wizard.closeConfirm.keepGoing')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmCloseOpen(false); onOpenChange?.(false); }}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t('routines.wizard.closeConfirm.discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
